import React, { useCallback } from 'react';
import { StyleSheet } from 'react-native';
import {
  type RouteProp,
  useNavigation,
  useRoute,
} from '@react-navigation/native';
import { theme, useResponsive } from '@sg/garnish';

import { useLocalizationContext } from '@order/Localization';
import { useA11ySelector } from '@order/utils';

import {
  AccountScreenBorderedSection,
  AccountScreenContainer,
  AccountScreenSection,
  AccountScreenSectionTitle,
  AccountScreenTitle,
  CreditSection,
  SignedOutView,
} from '../../components';
import { useIsLoggedIn } from '../../context/AuthMachine';
import {
  useCustomerCredit,
  usePromoCode,
  usePromoCodeDeepLink,
  useStipendCredits,
} from '../../hooks';
import { type AccountTabStackParamList } from '../../navigation/AppNavigation.props';

// ─────────────────────────────────────────────────────────────────────────────

export const CreditAndPromoCodesScreen = () => {
  const { navigate } = useNavigation();
  const { t } = useLocalizationContext();
  const { match, minWidth } = useResponsive();

  const containerA11ySelector = useA11ySelector('account.credit-and-promo.container'); // prettier-ignore

  const { params } =
    useRoute<RouteProp<AccountTabStackParamList, 'CreditAndPromoCodes'>>();

  usePromoCodeDeepLink(params?.promoCode);

  const { credits, isLoadingCredits, refetchCredits, availableCredit } =
    useCustomerCredit({
      requestPolicy: 'cache-and-network',
      shouldRefetchOnFocus: true,
    });

  // NOTE: We only use stipend credits.
  const { filteredCredits, totalCreditAmount } = useStipendCredits({
    credits,
    availableCredit,
  });

  const isLoggedIn = useIsLoggedIn();

  // ─── Promo Code ──────────────────────────────────────────────────────

  const {
    promoCode,
    promoCodeInvalid,
    isSubmittingPromoCode,
    isPromoCodeApplied,
    setPromoCode,
    submitPromoCode,
  } = usePromoCode(refetchCredits);

  // ─── Callbacks ───────────────────────────────────────────────────────

  const handlePressAvailableCredit = useCallback(() => {
    navigate('Modal', { screen: 'CreditDetails' });
  }, [navigate]);

  const handlePressAddPromoCode = useCallback(() => {
    navigate('Modal', { screen: 'CreditAdd' });
  }, [navigate]);

  // ─────────────────────────────────────────────────────────────────────

  const title = t('account.credit-and-promo-codes.title');

  // ─────────────────────────────────────────────────────────────────────

  if (!isLoggedIn) {
    return <SignedOutView redirectTo="credit-and-promo-codes" withoutFooter />;
  }

  return (
    <AccountScreenContainer
      screen="credit-and-promo-codes"
      {...containerA11ySelector.props}
    >
      <AccountScreenTitle visibility="COMPACT" title={title} />
      {minWidth.isSM ? <AccountScreenSectionTitle title={title} /> : null}

      <AccountScreenSection>
        <AccountScreenBorderedSection
          style={match([undefined, styles.sectionContainerSM])}
        >
          <CreditSection
            creditAmount={totalCreditAmount}
            // @ts-expect-error TS(2322): Type 'readonly { readonly __typename?: "Credit" | ... Remove this comment to see the full error message
            creditDetails={filteredCredits}
            promoCode={promoCode}
            isLoadingCredits={isLoadingCredits}
            isSubmittingPromoCode={isSubmittingPromoCode}
            isPromoCodeApplied={isPromoCodeApplied}
            hasPromoCodeError={promoCodeInvalid}
            onPressAvailableCredit={handlePressAvailableCredit}
            onPressAddPromoCode={handlePressAddPromoCode}
            setPromoCode={setPromoCode}
            onPressApplyPromoCode={submitPromoCode}
            isCollapsible={false}
          />
        </AccountScreenBorderedSection>
      </AccountScreenSection>
    </AccountScreenContainer>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  sectionContainerSM: {
    maxWidth: 600,
    paddingVertical: theme.spacing['6'],
  },
});
